/**
 * The instance's participant chain for InstanceContext (#685): loaded once per
 * instance and refetched on `participant-chain-changed` (the flow reached a
 * module, access changed, the chain was edited, a report was created) and when
 * the tab becomes visible again.
 *
 * It listens on the shared socket instead of opening its own, so every screen
 * does not get a second connection. Screens connect it themselves, possibly
 * after this mounts, so the listener attaches once the socket exists.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import SocketService from '../../../../packages/shared/socket';
import { fetchParticipantChain } from '../utils/participantEntry';

const SOCKET_POLL_MS = 3000;

export default function useParticipantChainState(instanceId) {
  const [chain, setChain] = useState(null);
  const [error, setError] = useState(null);
  const requestRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!instanceId) {
      setChain(null);
      setError(null);
      return null;
    }
    const request = ++requestRef.current;
    try {
      const data = await fetchParticipantChain(instanceId);
      if (request !== requestRef.current) return null;
      setChain(data);
      setError(null);
      return data;
    } catch (err) {
      if (request !== requestRef.current) return null;
      console.error('[participant-chain]', err);
      setChain(null);
      setError(err.message);
      return null;
    }
  }, [instanceId]);

  useEffect(() => {
    setChain(null);
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!instanceId) return undefined;
    let socket = null;
    const onChanged = (data) => {
      if (!data?.instanceId || data.instanceId === instanceId) refresh();
    };
    const attach = () => {
      if (socket || !SocketService.socket) return;
      socket = SocketService.socket;
      socket.emit('join-instance', { instanceId, deviceType: 'display' });
      socket.on('participant-chain-changed', onChanged);
    };
    attach();
    const timer = setInterval(() => {
      if (socket) clearInterval(timer);
      else attach();
    }, SOCKET_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      socket?.off('participant-chain-changed', onChanged);
    };
  }, [instanceId, refresh]);

  return { chain, error, refresh };
}
